import React from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { InformationLine } from "@ui/IconV2/react";
import type { DateValue } from "@internationalized/date";
import type { Preset } from "@ui/DateRangePicker/PresetSelector";
import Select, { type SelectOption } from "@ui/Select";
import {
  aggregationPeriodFromPresets,
  type AggregationPeriod,
} from "../../../domain/aggregation/period";

const getPresetValues = (preset: Preset<DateValue>) => ({
  start: typeof preset.startDate === "function" ? preset.startDate() : preset.startDate,
  end: typeof preset.endDate === "function" ? preset.endDate() : preset.endDate,
});

const WidgetSelectorHeader = ({
  title,
  tooltipText,
  aggregationPeriodPresets,
  aggregationIntervalDateValue,
  onAggregationIntervalChange,
  link,
  hidePeriod = true,
  disablePeriod = false,
}: {
  title: string;
  tooltipText?: string;
  aggregationPeriodPresets: Preset<DateValue, AggregationPeriod>[];
  aggregationIntervalDateValue: { start: DateValue; end: DateValue };
  onAggregationIntervalChange: (interval: { start: DateValue; end: DateValue }) => void;
  link?: React.ReactNode;
  hidePeriod?: boolean;
  disablePeriod?: boolean;
}) => {
  const selectedPreset = aggregationPeriodFromPresets({
    dateValues: aggregationIntervalDateValue,
    presets: aggregationPeriodPresets,
  });

  const options: SelectOption[] = aggregationPeriodPresets.map(preset => ({
    label: preset.label,
    value: preset.metadata?.id ?? preset.label,
  }));

  return (
    <div className={widgetSelectorHeader}>
      <div className={wrapperTitle}>
        <div className={titleWithIcon}>
          <Text type="body1SemiBold">{title}</Text>
          {tooltipText && (
            <TooltipV2
              placement="top"
              overlay={<OverlayWrapper className={overlay} text={tooltipText} />}
            >
              <span className={centerIcon}>
                <InformationLine size="s" color={colors.carbon800} />
              </span>
            </TooltipV2>
          )}
          {link}
        </div>
        {hidePeriod ? null : (
          <Select
            options={options}
            prompt=""
            value={selectedPreset ?? undefined}
            onChange={event => {
              const selected = aggregationPeriodPresets.find(
                preset => (preset.metadata?.id ?? preset.label) === event.target.value,
              );
              if (!selected) return;
              const { start, end } = getPresetValues(selected);
              onAggregationIntervalChange({ start, end });
            }}
            clearable={false}
            disabled={disablePeriod}
            className={periodSelect}
          />
        )}
      </div>
    </div>
  );
};
export default WidgetSelectorHeader;

//#region CSS
export const widgetSelectorHeader = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${spacings.xs};
  width: 100%;
  overflow: auto;
`;
const wrapperTitle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacings.xs};
  width: 100%;
`;
const titleWithIcon = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${colors.carbon800};
  flex-wrap: nowrap;

  & > :nth-child(1) {
    white-space: nowrap;
  }
`;
const centerIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const overlay = css`
  max-width: 350px;
`;

const periodSelect = css`
  min-width: 185px;
`;
//#endregion CSS
